import type { FormSubmitInput, FormsPacket, ResolvedAddress } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, errorMessage, get, post } from "../api";
import { FormsFill } from "../components/FormsFill";
import { DateOfBirthField, Alert, Button, Field, Loading } from "../components/ui";
import { time12 } from "../components/AppointmentDialog";

/** onStartOver: the iPad's "Start over" (back to its welcome screen), on every screen but the welcome. */
export function Frame({ clinic, children, title = "Forms", onStartOver }: { clinic: string; children: ReactNode; title?: string; onStartOver?: () => void }) {
  useEffect(() => {
    document.title = clinic ? `${title} · ${clinic}` : title;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [clinic, title]);
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-ink px-4 py-4 text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
          <span className="font-display text-xl">{clinic || " "}</span>
          {onStartOver && (
            <button type="button" onClick={onStartOver} className="rounded-[var(--radius-control)] border border-white/40 px-3 py-1.5 text-sm font-semibold active:bg-white/10">
              Start over
            </button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}

export const visitLine = (p: FormsPacket) =>
  p.appointment ? `${p.appointment.serviceName}, ${new Date(`${p.appointment.start.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })} at ${time12(p.appointment.start)}` : null;

/** Client's private forms link: date of birth first, then each form to sign. */
export function FormsPage() {
  const { token = "" } = useParams();
  const base = `/api/forms/${encodeURIComponent(token)}`;
  const info = useQuery({ queryKey: ["forms", token], queryFn: () => get<{ clinicName: string }>(base), retry: false });
  // Reopening within two hours skips the date of birth.
  // Straight from booking (signed in with a texted code): the link carries a pass instead of asking for the date of birth.
  const [handoff] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("k"));
  // Take the pass out of the address bar (and so out of bookmarks and shared links).
  useEffect(() => {
    if (handoff) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [handoff]);
  const handed = useQuery({ queryKey: ["forms-handoff", token], queryFn: () => post<FormsPacket>(`${base}/unlock`, { handoff }), retry: false, enabled: info.isSuccess && !!handoff });
  const resumed = useQuery({ queryKey: ["forms-packet", token], queryFn: () => get<FormsPacket>(`${base}/packet`), retry: false, enabled: info.isSuccess && (!handoff || handed.isError) });
  const [packet, setPacket] = useState<FormsPacket | null>(null);
  const [dob, setDob] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const current = packet ?? handed.data ?? resumed.data ?? null;
  const clinic = info.data?.clinicName ?? "";

  if (info.error) {
    return (
      <Frame clinic="">
        <Alert>{errorMessage(info.error)}</Alert>
      </Frame>
    );
  }
  if (!info.data || resumed.isLoading || handed.isLoading) {
    return (
      <Frame clinic={clinic}>
        <Loading />
      </Frame>
    );
  }
  if (!current) {
    const unlock = async () => {
      setBusy(true);
      setError(null);
      try {
        setPacket(await post<FormsPacket>(`${base}/unlock`, { dob }));
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    };
    return (
      <Frame clinic={clinic}>
        <form
          className="mx-auto flex max-w-sm flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void unlock();
          }}
        >
          <h1 className="font-display text-2xl">Your forms</h1>
          <p className="text-ink-soft">To keep your health information private, enter the date of birth of the person these forms are for.</p>
          <DateOfBirthField required value={dob} onChange={setDob} />
          {error && <Alert>{error}</Alert>}
          <Button variant="primary" type="submit" busy={busy}>
            Continue
          </Button>
        </form>
      </Frame>
    );
  }
  return (
    <Frame clinic={clinic}>
      <div className="mb-6">
        <h1 className="font-display text-2xl">Forms for {current.client.firstName}</h1>
        {visitLine(current) && <p className="text-ink-soft">{visitLine(current)}</p>}
        {current.client.minor && <p className="mt-1 text-sm font-semibold text-wait">{current.client.firstName} is under 18: a parent or guardian fills in and signs.</p>}
      </div>
      <FormsFill
        packet={current}
        onSubmit={(v: FormSubmitInput) => post<FormsPacket>(`${base}/submit`, v)}
        addressLookup={{
          suggest: (q) => post<{ id: string; label: string }[]>(`${base}/address/suggest`, { q }),
          resolve: (id) => post<ResolvedAddress>(`${base}/address/resolve`, { id }),
        }}
        doneNote="Thank you. Your forms are with us; nothing else is needed before your visit."
        closable
        screening={{
          start: async () => (await post<{ url: string | null }>(`${base}/screening`, {})).url,
          later: async () => {
            await post(`${base}/screening`, { later: true });
          },
        }}
      />
    </Frame>
  );
}

/** Check-in iPad: only this client's forms, then a screen to hand it back. */
export function KioskPage() {
  const { id = "" } = useParams();
  const packet = useQuery({ queryKey: ["kiosk", id], queryFn: () => get<FormsPacket>(`/api/appointments/${id}/forms`) });
  if (packet.error) {
    return (
      <Frame clinic="">
        <Alert>{errorMessage(packet.error)}</Alert>
        {packet.error instanceof ApiError && packet.error.status === 401 && <p className="mt-2 text-sm">Sign in again on this iPad.</p>}
      </Frame>
    );
  }
  if (!packet.data) {
    return (
      <Frame clinic="">
        <Loading />
      </Frame>
    );
  }
  const p = packet.data;
  return (
    <Frame clinic={p.clinicName}>
      <div className="mb-6">
        <h1 className="font-display text-2xl">Welcome, {p.client.firstName}</h1>
        {visitLine(p) && <p className="text-ink-soft">{visitLine(p)}</p>}
        {p.client.minor && <p className="mt-1 text-sm font-semibold text-wait">A parent or guardian fills in and signs for {p.client.firstName}.</p>}
      </div>
      <FormsFill packet={p} onSubmit={(v) => post<FormsPacket>(`/api/appointments/${id}/forms`, v)} doneNote="Thank you. Please hand the iPad back to our team." />
      <div className="mt-10 border-t border-line pt-4 text-right">
        <Link to="/front-desk" className="text-sm text-muted underline">
          Staff: back to front desk
        </Link>
      </div>
    </Frame>
  );
}
